const RING_PATH = "/users/doorbell/ring"

function csrfHeaders(csrfToken) {
  return {
    "Content-Type": "application/json",
    Accept: "application/json",
    ...(csrfToken ? { "X-CSRF-Token": csrfToken } : {}),
  }
}

function isAbortError(error) {
  return !!error && (error.name === "AbortError" || error.code === 20)
}

export async function ringDoorbell({ email, csrfToken, solutionSlug, loginContext }) {
  const res = await fetch(RING_PATH, {
    method: "POST",
    credentials: "same-origin",
    headers: csrfHeaders(csrfToken),
    body: JSON.stringify({
      email,
      ...(solutionSlug ? { solution_slug: solutionSlug } : {}),
      ...(loginContext ? { login_context: loginContext } : {}),
    }),
  })
  const data = await res.json().catch(() => ({}))
  return { ok: res.ok && data.ok, status: res.status, data }
}

export async function pollDoorbellStatus({ requestId, token }) {
  const url = `/users/doorbell/${encodeURIComponent(requestId)}/status?token=${encodeURIComponent(token)}`
  const res = await fetch(url, {
    method: "GET",
    credentials: "same-origin",
    headers: { Accept: "application/json" },
  })
  const data = await res.json().catch(() => ({}))
  return { ok: res.ok && data.ok, status: res.status, data }
}

export async function claimDoorbell({ requestId, token, csrfToken }) {
  const res = await fetch(`/users/doorbell/${encodeURIComponent(requestId)}/claim`, {
    method: "POST",
    credentials: "same-origin",
    headers: csrfHeaders(csrfToken),
    body: JSON.stringify({ token }),
  })
  const data = await res.json().catch(() => ({}))
  return { ok: res.ok && data.ok, status: res.status, data }
}

export async function fetchPendingDoorbells({ signal } = {}) {
  try {
    const res = await fetch("/users/doorbell/pending", {
      method: "GET",
      credentials: "same-origin",
      headers: { Accept: "application/json" },
      ...(signal ? { signal } : {}),
    })
    if (res.status === 401) return { ok: false, unauthorized: true, data: { requests: [] } }
    const data = await res.json().catch(() => ({}))
    return { ok: res.ok && data.ok, data }
  } catch (error) {
    if (isAbortError(error)) return { ok: false, aborted: true, data: { requests: [] } }
    return { ok: false, networkError: true, data: { requests: [] } }
  }
}

export async function answerDoorbell({ requestId, action, csrfToken }) {
  const path = action === "deny" ? "deny" : "approve"
  try {
    const res = await fetch(`/users/doorbell/${encodeURIComponent(requestId)}/${path}`, {
      method: "POST",
      credentials: "same-origin",
      headers: csrfHeaders(csrfToken),
      body: JSON.stringify({}),
    })
    const data = await res.json().catch(() => ({}))
    return { ok: res.ok && data.ok, data }
  } catch (_) {
    return { ok: false, networkError: true, data: {} }
  }
}

export function bootDeviseDoorbellForm() {
  const form = document.getElementById("doorbell-devise-form")
  if (!form || form.dataset.doorbellBound === "true") return
  form.dataset.doorbellBound = "true"

  const emailInput = form.querySelector("#doorbell-devise-email")
  const submitBtn = form.querySelector("#doorbell-devise-submit")
  const statusEl = form.querySelector("#doorbell-devise-status")
  const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute("content")
  const loginContext = form.dataset.loginContext || "devise"
  const solutionSlug = form.dataset.solutionSlug || ""
  let pollTimer = null

  const setStatus = (kind, message) => {
    if (!statusEl) return
    statusEl.hidden = !message
    statusEl.textContent = message || ""
    statusEl.dataset.kind = kind || ""
    statusEl.className =
      kind === "error"
        ? "text-sm text-red-800 bg-red-50 border border-red-200 rounded p-2 dark:bg-red-950/50 dark:border-red-800 dark:text-red-200"
        : "text-sm text-green-800 bg-green-50 border border-green-200 rounded p-2 dark:bg-green-900/30 dark:border-green-800 dark:text-green-200"
  }

  form.addEventListener("submit", async (event) => {
    event.preventDefault()
    if (pollTimer) {
      clearInterval(pollTimer)
      pollTimer = null
    }
    const email = (emailInput?.value || "").trim()
    if (!email) {
      setStatus("error", "Enter the email of the account you're already signed into.")
      return
    }
    submitBtn.disabled = true
    setStatus("success", "Ringing…")
    try {
      const ring = await ringDoorbell({ email, csrfToken, solutionSlug, loginContext })
      if (ring.status === 429) {
        setStatus("error", "Too many ding dongs. Wait a few minutes and try again.")
        return
      }
      if (!ring.ok || !ring.data?.request_id || !ring.data?.token) {
        setStatus("error", ring.data?.message || "Could not ring the doorbell.")
        return
      }
      setStatus("success", ring.data.message || "Waiting for the other session to let you in…")
      const requestId = ring.data.request_id
      const token = ring.data.token
      const deadline = Date.now() + (Number(ring.data.expires_in) || 120) * 1000

      const tick = async () => {
        if (Date.now() > deadline) {
          clearInterval(pollTimer)
          pollTimer = null
          setStatus("error", "Nobody answered. Try ringing again.")
          submitBtn.disabled = false
          return
        }
        const poll = await pollDoorbellStatus({ requestId, token })
        const state = poll.data?.status
        if (state === "approved") {
          clearInterval(pollTimer)
          pollTimer = null
          setStatus("success", "They're letting you in…")
          const claimed = await claimDoorbell({ requestId, token, csrfToken })
          if (claimed.ok && claimed.data?.redirect_to) {
            window.location.assign(claimed.data.redirect_to)
            return
          }
          setStatus("error", claimed.data?.message || "Could not finish sign-in.")
          submitBtn.disabled = false
          return
        }
        if (state === "denied") {
          clearInterval(pollTimer)
          pollTimer = null
          setStatus("error", "That session declined to let you in.")
          submitBtn.disabled = false
          return
        }
        if (state === "expired" || state === "claimed") {
          clearInterval(pollTimer)
          pollTimer = null
          setStatus("error", "Nobody answered. Try ringing again.")
          submitBtn.disabled = false
        }
      }

      pollTimer = setInterval(tick, 2000)
      tick()
    } catch (_) {
      setStatus("error", "Network error. Please try again.")
      submitBtn.disabled = false
    }
  })
}
